import React, { useState } from "react";
import CustomTooltip from "./Library/Tooltip";
import { Link, useSearchParams } from "react-router-dom";
import { queryClient } from "../queryClient";
import { backendApi } from "../api";

type responseData = {
  EN?: string;
  ES?: string;
  ZH?: string;
  context?: string;
};

const polypTypes = [
  "Tubular Adenoma",
  "Hyperplastic Polyp",
  "Sessile Serrated Polyp",
  "Traditional Serrated Adenoma",
  "Tubulovillous or villous adenoma",
];

type DataItem = {
  polypType: string;
  numberOfPolyps: number;
  isSizeTen: boolean;
  isSizeTwenty: boolean;
  isDysplasia: boolean;
};

const serratedTypes = [
  "Hyperplastic Polyp",
  "Sessile Serrated Polyp",
  "Traditional Serrated Adenoma",
];

const secondaryPolypTags = [
  "lt5Rectum",
  "lt5Proximal",
  "range5to9Rectum",
  "range5to9Proximal",
  "gte10Rectum",
  "gte10Proximal",
] as const;
type MyKeys = (typeof secondaryPolypTags)[number];
type SerratedDataItem = {
  polypType: string;
} & {
  [K in MyKeys]: number;
};

function copyToClipboard(text: string) {
  navigator.clipboard.writeText(text);
}

const ColonoscopyPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const isDemo = Boolean(searchParams.get("demo")) || false;
  React.useEffect(() => {
    if (isDemo) {
      const htmlElement = document.querySelector("html")!;
      const previousTheme = htmlElement.getAttribute("data-theme");
      htmlElement.setAttribute("data-theme", "light");
      return () => {
        if (previousTheme) {
          htmlElement.setAttribute("data-theme", previousTheme);
        } else {
          htmlElement.removeAttribute("data-theme");
        }
      };
    }
  }, [isDemo]);

  const [colonoscopyStage, setColonoscopyStage] = useState(0);

  const modalRef = React.useRef<HTMLDialogElement | null>(null);
  const [response, setResponse] = useState<responseData>({});

  const initEmptyData = () => ({
    mainData: polypTypes.map((type) => {
      return {
        polypType: type,
        numberOfPolyps: 0,
        isSizeTen: false,
        isSizeTwenty: false,
        isDysplasia: false,
      };
    }),
    serratedData: serratedTypes.map((type) => ({
      polypType: type,
      ...(Object.fromEntries(secondaryPolypTags.map((key) => [key, 0])) as {
        [K in MyKeys]: number;
      }),
    })),
  });

  const [stagesData, setStagesData] = useState(() => [initEmptyData()]);
  const setData = (newData: (something: DataItem[]) => DataItem[]) => {
    setStagesData((prevStagesData) =>
      prevStagesData.map((stageData, index) =>
        index === colonoscopyStage
          ? {
              ...stageData,
              mainData: newData(stageData.mainData),
            }
          : stageData
      )
    );
  };

  const setSerratedData = (
    newSerratedData: (something: SerratedDataItem[]) => SerratedDataItem[]
  ) => {
    setStagesData((prevStagesData) =>
      prevStagesData.map((stageData, index) =>
        index === colonoscopyStage
          ? {
              ...stageData,
              serratedData: newSerratedData(stageData.serratedData),
            }
          : stageData
      )
    );
  };

  const numSerratedPolypsMainTables = stagesData.map((stageData) =>
    stageData.mainData.reduce((prev, curr) => {
      if (serratedTypes.includes(curr.polypType)) {
        prev += curr.numberOfPolyps;
      }
      return prev;
    }, 0)
  );

  const getIsDataMatching = (stage: number) => {
    const getNumPolypsForType = (type: string) => {
      const polyp = stagesData[stage].serratedData.find(
        (predicate) => predicate.polypType === type
      );
      if (polyp === undefined) return 0;
      return secondaryPolypTags.reduce((prev, curr) => prev + polyp[curr], 0);
    };
    return stagesData[stage].serratedData.every((polyp) => {
      const primaryNum = stagesData[stage].mainData.find(
        (predicate) => predicate.polypType === polyp.polypType
      )?.numberOfPolyps;
      const secondaryNum = getNumPolypsForType(polyp.polypType);
      return primaryNum === secondaryNum;
    });
  };

  const handleSubmit = async () => {
    const response = await queryClient.fetchQuery({
      queryKey: ["table", stagesData, colonoscopyStage],
      queryFn: async () =>
        await backendApi<responseData>({
          method: "POST",
          url: "/algorithms-old/recommendations",
          data: { stagesData, colonoscopyStage },
        }),
      staleTime: 1000 * 60, // Stale after 1 minute
    });
    setResponse(response.data);
    modalRef.current?.showModal();
  };

  const handleReset = () => {
    setStagesData((prevStagesData) =>
      prevStagesData.map((stageData, index) =>
        index === colonoscopyStage ? initEmptyData() : stageData
      )
    );
  };
  return (
    <>
      <h1 className="text-3xl font-bold">
        Colonoscopy Follow-Up Recommendations
      </h1>
      <h3 className="text-xl font-bold mt-4">
        {colonoscopyStage === 0
          ? "Baseline Findings"
          : "1st Surveillance Findings"}
      </h3>
      <Table
        data={stagesData[colonoscopyStage].mainData}
        setData={setData}
        serratedData={stagesData[colonoscopyStage].serratedData}
        setSerratedData={setSerratedData}
        numSerratedPolypsMainTable={
          numSerratedPolypsMainTables[colonoscopyStage]
        }
      />
      <button
        className="btn mb-6"
        onClick={handleSubmit}
        disabled={
          numSerratedPolypsMainTables[colonoscopyStage] >= 5 &&
          !getIsDataMatching(colonoscopyStage)
        }
      >
        Get {colonoscopyStage === 0 ? "1st" : "2nd"} Surveillance
        Recommendations
      </button>
      <button className="btn btn-ghost mb-6 ml-4" onClick={handleReset}>
        Reset
      </button>
      <button
        className="btn mb-6 ml-4"
        onClick={() => {
          if (colonoscopyStage === 0) {
            setStagesData([...stagesData, initEmptyData()]);
            setColonoscopyStage(1);
          } else {
            const newStagesData = [...stagesData];
            newStagesData.pop();
            setStagesData([...newStagesData]);
            setColonoscopyStage(0);
          }
        }}
        // disabled={
        //   numSerratedPolypsMainTables[colonoscopyStage] >= 5 &&
        //   !getIsDataMatching(colonoscopyStage)
        // }
        disabled
      >
        {colonoscopyStage === 0 ? "Add" : "Remove"} 1st Surveillance Data
        (Coming Soon)
      </button>

      {!isDemo && (
        <Link to={"/"} className="btn btn-link">
          Back
        </Link>
      )}
      <dialog ref={modalRef} className="modal">
        <div className="modal-box w-11/12 max-w-5xl">
          <h3 className="font-bold text-lg">Recommendation Email</h3>
          <p
            className="py-4"
            style={{
              textAlign: "left",
              fontFamily: "monospace,serif",
              fontSize: "0.75em",
              whiteSpace: "pre-line",
            }}
          >
            {/* Press ESC key or click the button below to close */}
            {response && response.EN}
          </p>
          <div className="modal-action">
            <CustomTooltip content="Copied!" trigger="click">
              <button
                className="btn"
                onClick={() => copyToClipboard(response.EN || "")}
              >
                Copy
              </button>
            </CustomTooltip>
            <form method="dialog">
              {/* if there is a button in form, it will close the modal */}
              <button className="btn">Close</button>
            </form>
          </div>
        </div>
      </dialog>
    </>
  );
};

function Table({
  data,
  setData,
  serratedData,
  setSerratedData,
  numSerratedPolypsMainTable,
}: {
  data: DataItem[];
  setData: (func: (data: DataItem[]) => DataItem[]) => void;
  serratedData: SerratedDataItem[];
  setSerratedData: (
    func: (data: SerratedDataItem[]) => SerratedDataItem[]
  ) => void;
  numSerratedPolypsMainTable: number;
}) {
  const handleNumberOfPolypsChange = (
    polypType: string,
    newNumberOfPolyps: string
  ) => {
    const sanitizedNumberOfPolyps = Math.max(
      0,
      parseInt(newNumberOfPolyps, 10) || 0
    );

    setData((prevData: DataItem[]) =>
      prevData.map((item) =>
        item.polypType === polypType
          ? { ...item, numberOfPolyps: sanitizedNumberOfPolyps }
          : item
      )
    );
  };

  const handleIsSizeTenChange = (polypType: string) => {
    setData((prevData) =>
      prevData.map((item) =>
        item.polypType === polypType
          ? { ...item, isSizeTen: !item.isSizeTen }
          : item
      )
    );
  };

  const handleIsSizeTwentyChange = (polypType: string) => {
    setData((prevData) =>
      prevData.map((item) =>
        item.polypType === polypType
          ? { ...item, isSizeTwenty: !item.isSizeTwenty }
          : item
      )
    );
  };

  const handleIsDysplasiaChange = (polypType: string) => {
    setData((prevData) =>
      prevData.map((item) =>
        item.polypType === polypType
          ? { ...item, isDysplasia: !item.isDysplasia }
          : item
      )
    );
  };

  return (
    <>
      <div className="overflow-x-auto">
        <table className="table table-zebra my-8">
          <thead>
            <tr>
              <th>Polyp type</th>
              <th>Number of polyps</th>
              <th>Size &ge; 10 mm</th>
              <th>Size &gt; 20 mm and Piecemeal</th>
              <th>Presence of Dysplasia</th>
            </tr>
          </thead>
          <tbody>
            {data.map((item) => (
              <tr key={item.polypType}>
                <td>{item.polypType}</td>
                <td>
                  <input
                    type="number"
                    className="input input-bordered input-xs w-full max-w-xs"
                    value={item.numberOfPolyps}
                    onChange={(e) =>
                      handleNumberOfPolypsChange(item.polypType, e.target.value)
                    }
                  />
                </td>
                <td>
                  <input
                    type="checkbox"
                    className="checkbox"
                    checked={item.isSizeTen}
                    onChange={() => handleIsSizeTenChange(item.polypType)}
                  />
                </td>
                <td>
                  <input
                    type="checkbox"
                    className="checkbox"
                    checked={item.isSizeTwenty}
                    onChange={() => handleIsSizeTwentyChange(item.polypType)}
                  />
                </td>
                <td>
                  <input
                    type="checkbox"
                    className="checkbox"
                    checked={item.isDysplasia}
                    onChange={() => handleIsDysplasiaChange(item.polypType)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {numSerratedPolypsMainTable >= 5 && (
        <>
          <h3 className="text-xl">SPS Risk Check</h3>
          <div className="my-8 overflow-x-auto rounded-md">
            <table className="table">
              <thead>
                <tr className="divide-x divide-base-300/50">
                  <th rowSpan={2} className="bg-base-200">
                    Polyp type
                  </th>
                  <th className="text-center bg-base-300" colSpan={2}>
                    &lt;5mm
                  </th>
                  <th className="text-center bg-base-200" colSpan={2}>
                    5-9mm
                  </th>
                  <th className="text-center bg-base-300" colSpan={2}>
                    &ge;10 mm
                  </th>
                </tr>
                <tr className="divide-x divide-base-300/50">
                  <th className="bg-base-300">Rectum</th>
                  <th className="bg-base-300">Proximal</th>
                  <th className="bg-base-200">Rectum</th>
                  <th className="bg-base-200">Proximal</th>
                  <th className="bg-base-300">Rectum</th>
                  <th className="bg-base-300">Proximal</th>
                </tr>
              </thead>
              <tbody>
                {serratedData.map((item) => (
                  <tr
                    key={item.polypType}
                    className="divide-x divide-base-300/40"
                  >
                    <td className="bg-base-200">{item.polypType}</td>
                    {secondaryPolypTags.map((polypTag, i) => (
                      <td
                        key={polypTag + item.polypType}
                        className={i % 4 < 2 ? "bg-base-300" : "bg-base-200"}
                      >
                        <input
                          type="number"
                          className="input input-bordered input-xs w-full max-w-xs"
                          value={item[polypTag]}
                          onChange={(e) => {
                            setSerratedData((prevData) =>
                              prevData.map((curr) =>
                                curr.polypType === item.polypType
                                  ? {
                                      ...curr,
                                      [polypTag]: Math.max(
                                        0,
                                        parseInt(e.target.value, 10) || 0
                                      ),
                                    }
                                  : curr
                              )
                            );
                          }}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-xs mt-2 italic">
              *Proximal: Sigmoid, Descending, Transverse, Ascending, Cecum
            </p>
          </div>
        </>
      )}
    </>
  );
}

export default ColonoscopyPage;
